import React, { useState, useEffect } from "react";
import { Star, Leaf, Shield, Sparkles } from "lucide-react";
import { productsAPI, handleAPIError } from "../services/api";
import OrderModal from "./OrderModal";
import { stripeConfigFor } from "../config/stripe";

// Legacy fallback: hosted Stripe Payment Links (still work for any product
// that doesn't yet have an embedded Buy Button id in config/stripe.js).
const LEGACY_STRIPE_LINKS = {
  "Ka Lā 'Alohi": "https://buy.stripe.com/4gM7sKezNc9PcSHg4pcs802",
  "Princess Pikake": "https://buy.stripe.com/4gM7sKezNc9PcSHg4pcs802",
};

const legacyStripeLinkFor = (product) => LEGACY_STRIPE_LINKS[product?.name] || null;

const INGREDIENT_CATEGORIES = [
  {
    name: "Nourishing Oils",
    description: "Premium plant-based oils that deeply hydrate and restore skin's natural barrier",
    items: ["Coconut Oil", "Kukui Nut Oil"],
    icon: <Leaf size={24} />
  },
  {
    name: "Active Nutrients",
    description: "Vitamins and antioxidants that protect and revitalize skin health",
    items: ["Vitamin E", "Essential Amino Acids", "Natural Antioxidants"],
    icon: <Shield size={24} />
  },
  {
    name: "Natural Minerals",
    description: "Pure minerals that enhance skin's natural luminosity",
    items: ["Mica", "Natural Shimmer Particles"],
    icon: <Sparkles size={24} />
  }
];

const SECTION_BG = "/images/legacy-skincare-1.png";

/* ---------- sub-components ---------- */

const SectionShell = ({ children, subtitle }) => (
  <section id="products" className="section-padding products-ingredients-section">
    <div className="section-background">
      <img src={SECTION_BG} alt="Ka Lā 'Alohi luxury skincare background" className="section-background-image" />
      <div className="section-overlay"></div>
    </div>
    <div className="container section-content">
      <div className="unified-luxury-section">
        <div className="section-header">
          <h2 className="heading-1">Island Luxury Collection</h2>
          <p className="body-large section-subtitle">{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  </section>
);

const ProductCardInline = ({ product, onBuy }) => (
  <div className="product-card-inline product-card-typo">
    <div className="product-info-inline">
      <h4 className="product-name product-name-display">{product.name}</h4>
      <p className="product-subtitle-small">{product.subtitle}</p>

      <div className="product-rating">
        {[...Array(5)].map((_, index) => (
          <Star
            key={`star-${product.id}-${index}`}
            size={16}
            className="star-filled"
            fill="currentColor"
          />
        ))}
        <span className="rating-text">(5.0 stars)</span>
      </div>

      <p className="product-description-brief">{product.description}</p>
      <div className="product-shake-instruction-inline">
        <p className="body-small shake-text-small"><em>Shake Well, separation is Natural</em></p>
      </div>
      <div className="product-price-inline">
        <span className="price-amount-small">{product.price}</span>
      </div>

      <div className="product-details-inline product-details-always">
        <h5 className="heading-3">Key Ingredients</h5>
        <div className="ingredients-list-inline">
          {product.key_ingredients.map((ingredient) => (
            <div key={ingredient.name} className="ingredient-item-inline">
              <strong>{ingredient.name}:</strong> {ingredient.benefit}
            </div>
          ))}
        </div>
      </div>

      <div className="product-actions-inline">
        {stripeConfigFor(product.name) ? (
          <div
            className="stripe-buy-button-wrap"
            data-testid={`buy-now-${product.id}`}
          >
            <stripe-buy-button
              buy-button-id={stripeConfigFor(product.name).buyButtonId}
              publishable-key={stripeConfigFor(product.name).publishableKey}
            ></stripe-buy-button>
          </div>
        ) : legacyStripeLinkFor(product) ? (
          <a
            href={legacyStripeLinkFor(product)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary small buy-now-btn"
            data-testid={`buy-now-${product.id}`}
          >
            Buy Now
          </a>
        ) : (
          <button
            className="btn-primary small buy-now-btn"
            onClick={onBuy}
            data-testid={`buy-now-${product.id}`}
          >
            Buy Now
          </button>
        )}
      </div>
    </div>
  </div>
);

const IngredientCategory = ({ category }) => (
  <div className="ingredient-category-compact">
    <div className="category-header">
      <div className="category-icon-small">{category.icon}</div>
      <h4 className="heading-3">{category.name}</h4>
    </div>
    <p className="category-description-small">{category.description}</p>
    <ul className="ingredient-items-compact">
      {category.items.map((item) => (
        <li key={item} className="ingredient-item-compact">{item}</li>
      ))}
    </ul>
  </div>
);

const IngredientsColumn = () => (
  <div className="ingredients-column">
    <h3 className="heading-2">Pure Island Ingredients</h3>
    <p className="ingredients-philosophy-text">
      Every ingredient is carefully selected for its proven benefits, sourced with respect
      for the environment and Hawaiian communities.
    </p>

    <div className="ingredients-categories-compact">
      {INGREDIENT_CATEGORIES.map((category) => (
        <IngredientCategory key={category.name} category={category} />
      ))}
    </div>
  </div>
);

/* ---------- main component ---------- */

const ProductsAndIngredientsSection = () => {
  const [products, setProducts] = useState([]);
  const [orderProduct, setOrderProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const response = await productsAPI.getAll();
        if (active) setProducts(response.data);
      } catch (err) {
        if (active) setError(handleAPIError(err));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading || error) {
    return <SectionShell subtitle="Handcrafted Hawaiian skincare with nature's finest ingredients" />;
  }

  return (
    <>
      <SectionShell subtitle="Handcrafted Hawaiian body oils made with nature's finest ingredients">
        <div className="luxury-content-grid">
          <div className="products-column">
            <h3 className="heading-2">Luxurious Body Oils</h3>
            <div className="products-list">
              {products.map((product) => (
                <ProductCardInline
                  key={product.id}
                  product={product}
                  onBuy={() => setOrderProduct(product)}
                />
              ))}
            </div>
          </div>

          <IngredientsColumn />
        </div>
      </SectionShell>

      {orderProduct && (
        <OrderModal
          product={orderProduct}
          onClose={() => setOrderProduct(null)}
        />
      )}
    </>
  );
};

export default ProductsAndIngredientsSection;
